// src/components/ClientDocumentsData.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import MUIDataTable from "mui-datatables";
import { Icon } from "@iconify/react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getAssessments,
  deleteAssessment,
  downloadAssessmentPDF,
  clearDocumentState,
} from "../redux/slices/documentSlice";
import { getMyFacilities } from "../redux/slices/facilitySlice";
import PDFIcon from "../otherImages/pdf-icon.svg";
import Toast from "../components/Toast";
import DateFilter from "./DateFilter";
import useDateFilter from "./useDateFilter";
import DocumentPreviewPanel from "./DocumentPreviewPanel";

// Index of the hidden assessmentId column inside tableMeta.rowData
const ASSESSMENT_ID_COL_INDEX = 0;

const ClientDocumentsData = forwardRef((props, ref) => {
  const dispatch = useDispatch();

  const {
    assessments,
    isLoading,
    isDeleting,
    isDownloadingPDF,
    error,
    deleteError,
    downloadPDFError,
    successMessage,
  } = useSelector((state) => state.document);

  const {
    myFacilities = { data: [] },
    isLoading: isLoadingFacilities = false,
  } = useSelector((state) => state.facility || {});

  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState("all");
  // For searchable dropdown
  const [facilitySearch, setFacilitySearch] = useState("");
  const [showFacilityDropdown, setShowFacilityDropdown] = useState(false);
  const facilityDropdownRef = useRef(null);

  const { dateFilter, updateFilter, getDateRange } = useDateFilter();

  // Store the assessmentId of the row whose dropdown is open.
  // Using the ID (instead of an index) keeps the dropdown correct even
  // when the table re-sorts rows internally.
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const buttonRefs = useRef({});

  const [previewAssessment, setPreviewAssessment] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Fetch all facilities (increase per_page to get all)
  useEffect(() => {
    dispatch(getMyFacilities({ page: 1, per_page: 10000 }));
  }, [dispatch]);

  useEffect(() => {
    if (myFacilities.data && myFacilities.data.length > 0) {
      const facilityList = myFacilities.data.map((facility) => ({
        id: facility.facility_id,
        name: facility.facility_name,
      }));
      setFacilities(facilityList);
    }
  }, [myFacilities]);

  // Function to fetch assessment data (used internally and by parent)
  const fetchAssessmentData = () => {
    const dateRange = getDateRange();
    const params = {
      page: currentPage,
      from_date: dateRange.from_date,
      to_date: dateRange.to_date,
    };
    if (selectedFacilityId !== "all") {
      params.facility_id = selectedFacilityId;
    }
    dispatch(getAssessments(params));
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getFilterParams: () => {
      const dateRange = getDateRange();
      const params = {
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
      };

      if (selectedFacilityId !== "all") {
        params.facility_id = selectedFacilityId;
      }

      return params;
    },
    refreshData: () => {
      fetchAssessmentData();
    },
  }));

  // Fetch assessments with date and facility filters
  useEffect(() => {
    fetchAssessmentData();
  }, [
    dispatch,
    currentPage,
    dateFilter.selectedYear,
    dateFilter.viewType,
    dateFilter.selectedQuarter,
    dateFilter.selectedMonth,
    selectedFacilityId,
  ]);

  // Toast messages
  useEffect(() => {
    if (successMessage) {
      showToast(successMessage, "success");
      dispatch(clearDocumentState());
    }
    if (error) {
      showToast(error, "error");
      dispatch(clearDocumentState());
    }
    if (deleteError) {
      showToast(deleteError, "error");
      dispatch(clearDocumentState());
    }
    if (downloadPDFError) {
      showToast(downloadPDFError, "error");
      dispatch(clearDocumentState());
    }
  }, [successMessage, error, deleteError, downloadPDFError, dispatch]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const handleFacilityChange = (facilityId) => {
    setSelectedFacilityId(facilityId);
    setCurrentPage(1);
    setFacilitySearch("");
    setShowFacilityDropdown(false);
  };

  const handlePreview = (assessment) => {
    setPreviewAssessment(assessment);
    setIsPreviewOpen(true);
    setDropdownOpen(null);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setTimeout(() => setPreviewAssessment(null), 300);
  };

  const handleDownload = async (assessment) => {
    try {
      const assessmentId =
        assessment.assessmentId ||
        assessment.originalData?.assessment_id ||
        assessment.originalData?.id;
      if (!assessmentId) {
        showToast("Cannot download: Missing assessment ID", "error");
        return;
      }
      dispatch(downloadAssessmentPDF(assessment)).then((result) => {
        if (result.payload?.success) {
          showToast("PDF downloaded successfully", "success");
        }
      });
    } catch (error) {
      showToast(`Error downloading PDF: ${error.message}`, "error");
    } finally {
      setDropdownOpen(null);
    }
  };

  const handleDropdownToggle = (assessment, e) => {
    e.stopPropagation();

    if (buttonRefs.current[assessment.assessmentId]) {
      const rect = buttonRefs.current[assessment.assessmentId].getBoundingClientRect();
      setButtonPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX - 120,
      });
    }

    setDropdownOpen(
      dropdownOpen === assessment.assessmentId
        ? null
        : assessment.assessmentId,
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        facilityDropdownRef.current &&
        !facilityDropdownRef.current.contains(event.target)
      ) {
        setShowFacilityDropdown(false);
      }
      setDropdownOpen(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleDelete = (assessment) => {
    const assessmentId =
      assessment.assessmentId ||
      assessment.originalData?.assessment_id ||
      assessment.originalData?.id;
    if (!assessmentId) {
      showToast("Cannot delete: Missing assessment ID", "error");
      setDropdownOpen(null);
      return;
    }
    if (
      window.confirm(
        `Delete "${assessment.documentName || "this document"}"? This action cannot be undone.`,
      )
    ) {
      dispatch(deleteAssessment(assessmentId)).then((result) => {
        if (result.payload?.success) {
          showToast(
            `Document "${assessment.documentName}" deleted successfully`,
            "success",
          );
          const dateRange = getDateRange();
          const params = {
            page: currentPage,
            from_date: dateRange.from_date,
            to_date: dateRange.to_date,
          };
          if (selectedFacilityId !== "all")
            params.facility_id = selectedFacilityId;
          dispatch(getAssessments(params));
        }
      });
    }
    setDropdownOpen(null);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "N/A";
    }
  };

  const transformAssessmentData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];
    return apiData.map((assessment) => {
      // Document name is the assessment category name
      // (e.g. "Kitchen Sanitation Assessment", "Meal Observation Assessment")
      let documentName = assessment.category_name || "Unnamed Document";

      return {
        id: assessment.id,
        assessmentId: assessment.assessment_id || assessment.id,
        documentName: documentName,
        uploadedBy:
          assessment.user_name || assessment.user?.name || "Unknown User",
        userId: assessment.user_id || assessment.user?.id || "N/A",
        uploadDate: formatDate(assessment.created_at || assessment.upload_date),
        uploadTime: formatTime(assessment.created_at || assessment.upload_date),
        description: assessment.description || "",
        pdf_url: assessment.pdf_url || assessment.file_url,
        facility_name: assessment.facility_name || "Unknown Facility",
        category_name: assessment.category_name || "Uncategorized",
        customer_group_name: assessment.customer_group_name || "N/A",
        score: assessment.score || 0,
        max_score: assessment.max_score || 0,
        originalData: assessment,
      };
    });
  };

  const documentData = isLoading
    ? []
    : assessments.data && Array.isArray(assessments.data)
      ? transformAssessmentData(assessments.data)
      : [];

  // Look up a full row object by its assessmentId.
  // This is what keeps every custom renderer correct even when
  // mui-datatables re-sorts rows internally.
  const rowLookup = useMemo(() => {
    const lookup = {};
    documentData.forEach((row) => {
      if (row.assessmentId !== undefined && row.assessmentId !== null) {
        lookup[row.assessmentId] = row;
      }
    });
    return lookup;
  }, [documentData]);

  const columns = [
    // Hidden column used ONLY as a stable key for row lookups.
    // Its value travels with each row through sorting, filtering
    // and searching, so renderers can always resolve the correct
    // original row data.
    {
      name: "assessmentId",
      label: "Assessment ID",
      options: {
        display: "false",
        filter: false,
        searchable: false,
        sort: false,
        print: false,
        download: false,
        viewColumns: false,
      },
    },
    { name: "facility_name", label: "Facility Name" },
    {
      name: "customer_group_name",
      label: "Customer Group",
      options: {
        sort: false,
      },
    },
    {
      name: "score",
      label: "Score",
      options: {
        sort: false,
        customBodyRender: (value, tableMeta) => {
          // tableMeta.rowIndex is the SORTED position, so never use it
          // to index documentData. Resolve the original row via the
          // hidden assessmentId that travels with this row.
          const rowId = tableMeta?.rowData?.[ASSESSMENT_ID_COL_INDEX];
          const row = rowLookup[rowId];
          const rawScore = row ? row.score : value;
          const score = rawScore || 0;
          const maxScore = row?.max_score || 0;
          const percentage =
            maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
          const getScoreColor = (pct) => (pct < 80 ? "#dc3545" : "#28a745");
          const scoreColor = getScoreColor(percentage);
          return (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span
                style={{
                  fontWeight: 600,
                  color: scoreColor,
                  fontSize: "1rem",
                }}>
                {percentage}%
              </span>
              <span
                style={{ fontSize: "0.7rem", color: "#666", marginTop: "2px" }}>
                ({score} / {maxScore})
              </span>
            </div>
          );
        },
      },
    },
    {
      name: "documentName",
      label: "Document Name",
      options: {
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const rowId = tableMeta?.rowData?.[ASSESSMENT_ID_COL_INDEX];
          const rowData = rowLookup[rowId];
          return (
            <div className="col-clientName d-flex align-items-center gap-8">
              <img
                style={{ height: "35px", width: "35px" }}
                src={PDFIcon}
                alt="pdfImage"
              />
              <div>
                <div style={{ fontWeight: "500" }}>{value}</div>
                {rowData?.assessmentId && (
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#999",
                      marginTop: "2px",
                    }}>
                    Assessment ID: {rowData.assessmentId}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
    },
    {
      name: "uploadedBy",
      label: "Uploaded By",
      options: {
        sort: false,
      },
    },
    {
      name: "uploadDate",
      label: "Uploaded Date",
      options: {
        sort: false,
      },
    },
    {
      name: "uploadTime",
      label: "Uploaded Time",
      options: {
        sort: false,
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value, tableMeta) => {
          // Resolve the original row via the hidden assessmentId that
          // travels with this row through sorting, filtering and searching.
          const rowId = tableMeta?.rowData?.[ASSESSMENT_ID_COL_INDEX];
          const rowData = rowLookup[rowId];

          if (!rowData) return null;

          return (
            <div style={{ position: "relative" }}>
              <button
                ref={(el) => {
                  if (el && rowData.assessmentId != null) {
                    buttonRefs.current[rowData.assessmentId] = el;
                  }
                }}
                onClick={(e) => handleDropdownToggle(rowData, e)}
                disabled={isDeleting || isDownloadingPDF}
                style={{
                  background: "none",
                  borderRadius: "4px",
                  cursor:
                    isDeleting || isDownloadingPDF ? "not-allowed" : "pointer",
                  padding: "4px 8px",
                  opacity: isDeleting || isDownloadingPDF ? 0.6 : 1,
                }}>
                <Icon icon="mdi:dots-horizontal" width="20" height="20" />
              </button>
            </div>
          );
        },
      },
    },
  ];

  const options = {
    selectableRows: "none",
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    serverSide: false,
    searchPlaceholder: "Search documents...",
    pagination: false,
    tableBodyHeight: "auto",
    count: assessments.total || 0,
    rowsPerPage: assessments.per_page || 10,
    rowsPerPageOptions: [5, 10, 25, 50],
    page: currentPage - 1,
    onPageChange: (page) => handlePageChange(page + 1),
    onTableChange: (action, tableState) => {
      if (action === "changePage") handlePageChange(tableState.page + 1);
    },
    setRowProps: (row, dataIndex) => ({
      style: { backgroundColor: dataIndex % 2 === 0 ? "#f9f9f9" : "white" },
    }),
    textLabels: {
      body: {
        noMatch: isLoading
          ? "Loading..."
          : "No documents found for selected period",
      },
    },
  };

  // Filter facilities based on search input
  const filteredFacilities = facilities.filter((f) =>
    f.name.toLowerCase().includes(facilitySearch.toLowerCase()),
  );

  const renderFilterSection = () => (
    <div className="mb-4 pb-2 border-bottom">
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <div style={{ minWidth: "300px" }}>
          <DateFilter {...dateFilter} onFilterChange={updateFilter} size="sm" />
        </div>

        {/* Searchable Facility Dropdown */}
        <div style={{ minWidth: "250px" }} ref={facilityDropdownRef}>
          <div style={{ position: "relative" }}>
            <div
              onClick={() => setShowFacilityDropdown(!showFacilityDropdown)}
              style={{
                padding: "8px 12px",
                border: "1px solid #E0E0E0",
                borderRadius: "8px",
                backgroundColor: "white",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                fontSize: "14px",
                minHeight: "38px",
              }}>
              <span>
                {selectedFacilityId === "all"
                  ? "All Facilities"
                  : facilities.find(
                      (f) => f.id === parseInt(selectedFacilityId),
                    )?.name || "Select Facility"}
              </span>
              <Icon icon="mdi:chevron-down" width="20" height="20" />
            </div>
            {showFacilityDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  backgroundColor: "white",
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  zIndex: 1000,
                  marginTop: "4px",
                  maxHeight: "300px",
                  overflowY: "auto",
                }}>
                <div
                  style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>
                  <input
                    type="text"
                    placeholder="Search facility..."
                    value={facilitySearch}
                    onChange={(e) => setFacilitySearch(e.target.value)}
                    autoFocus
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div
                  style={{
                    cursor: "pointer",
                    padding: "10px 12px",
                    borderBottom: "1px solid #f5f5f5",
                  }}
                  onClick={() => handleFacilityChange("all")}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f5f5f5")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "white")
                  }>
                  All Facilities
                </div>
                {filteredFacilities.length === 0 && (
                  <div style={{ padding: "10px 12px", color: "#999" }}>
                    No facilities found
                  </div>
                )}
                {filteredFacilities.map((facility) => (
                  <div
                    key={facility.id}
                    style={{
                      cursor: "pointer",
                      padding: "10px 12px",
                      borderBottom: "1px solid #f5f5f5",
                    }}
                    onClick={() => handleFacilityChange(facility.id.toString())}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "white")
                    }>
                    {facility.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
        <span className="badge bg-light">
          <i className="fas fa-calendar-alt me-1"></i>
          {getDateRange().label}
        </span>

        {selectedFacilityId !== "all" && (
          <span className="badge bg-info">
            <i className="fas fa-building me-1"></i>
            Facility:{" "}
            {facilities.find((f) => f.id === parseInt(selectedFacilityId))
              ?.name || selectedFacilityId}
            <button
              onClick={() => handleFacilityChange("all")}
              style={{
                background: "none",
                border: "none",
                color: "white",
                marginLeft: "8px",
                padding: "0 4px",
                cursor: "pointer",
                fontSize: "14px",
              }}>
              ×
            </button>
          </span>
        )}

        {assessments.total > 0 && (
          <span className="badge bg-success">
            <i className="fas fa-file-pdf me-1"></i>
            {assessments.total} Documents
          </span>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    if (isLoading && !documentData.length) {
      return (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: "10px" }}>Loading documents...</p>
        </div>
      );
    }
    if (error && !documentData.length) {
      return (
        <div style={{ textAlign: "center", padding: "40px", color: "#D32F2F" }}>
          <Icon icon="material-symbols:error-outline" width="48" height="48" />
          <p style={{ marginTop: "10px" }}>Error: {error}</p>
          <button
            onClick={() => {
              const dateRange = getDateRange();
              const params = {
                page: 1,
                from_date: dateRange.from_date,
                to_date: dateRange.to_date,
              };
              if (selectedFacilityId !== "all")
                params.facility_id = selectedFacilityId;
              dispatch(getAssessments(params));
            }}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              backgroundColor: "#8B2885",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}>
            Retry
          </button>
        </div>
      );
    }
    if (!documentData.length && !isLoading) {
      return (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          <Icon
            icon="material-symbols:description-outline"
            width="48"
            height="48"
          />
          <p style={{ marginTop: "10px" }}>
            {selectedFacilityId !== "all"
              ? `No documents found for the selected facility in ${getDateRange().label}.`
              : `No documents found for ${getDateRange().label}.`}
          </p>
          <button
            onClick={() => {
              const dateRange = getDateRange();
              const params = {
                page: 1,
                from_date: dateRange.from_date,
                to_date: dateRange.to_date,
              };
              if (selectedFacilityId !== "all")
                params.facility_id = selectedFacilityId;
              dispatch(getAssessments(params));
            }}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              backgroundColor: "#8B2885",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}>
            Refresh
          </button>
        </div>
      );
    }
    return (
      <div className="basic-data-table">
        <MUIDataTable
          title=""
          data={documentData}
          columns={columns}
          options={options}
          className="overflow-hidden packageTable"
        />
      </div>
    );
  };

  return (
    <>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: "", type: "" })}
      />
      {renderFilterSection()}
      {renderContent()}

      {dropdownOpen !== null &&
        createPortal(
          <div
            style={{
              position: "absolute",
              top: buttonPosition.top,
              left: buttonPosition.left,
              backgroundColor: "white",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              minWidth: "180px",
              zIndex: 9999,
            }}
            onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                padding: "10px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "14px",
                color: "#333",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F5F5F5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
              onClick={() => handlePreview(rowLookup[dropdownOpen])}>
              <Icon icon="mdi:sidebar" width="18" height="18" color="#666" />
              <span>Quick Preview</span>
            </div>
            <div
              style={{
                padding: "10px 16px",
                cursor: isDownloadingPDF ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "14px",
                color: isDownloadingPDF ? "#999" : "#333",
                opacity: isDownloadingPDF ? 0.6 : 1,
              }}
              onMouseEnter={(e) =>
                !isDownloadingPDF &&
                (e.currentTarget.style.backgroundColor = "#F5F5F5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
              onClick={() =>
                !isDownloadingPDF && handleDownload(rowLookup[dropdownOpen])
              }>
              {isDownloadingPDF ? (
                <>
                  <div
                    className="spinner-border spinner-border-sm"
                    style={{
                      width: "18px",
                      height: "18px",
                      borderColor: "#666",
                    }}
                    role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <Icon
                    icon="mdi:download-outline"
                    width="18"
                    height="18"
                    color="#666"
                  />
                  <span>Download PDF</span>
                </>
              )}
            </div>
            <div
              style={{
                padding: "10px 16px",
                cursor:
                  isDeleting || isDownloadingPDF ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "14px",
                color: "#D32F2F",
                borderTop: "1px solid #F0F0F0",
                opacity: isDeleting || isDownloadingPDF ? 0.6 : 1,
              }}
              onMouseEnter={(e) =>
                !isDeleting &&
                !isDownloadingPDF &&
                (e.currentTarget.style.backgroundColor = "#FFE5E5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
              onClick={() =>
                !isDeleting &&
                !isDownloadingPDF &&
                handleDelete(rowLookup[dropdownOpen])
              }>
              {isDeleting ? (
                <>
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    style={{ borderColor: "#D32F2F" }}
                    role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Deleting...
                </>
              ) : (
                <>
                  <Icon
                    icon="material-symbols:delete-outline"
                    width="18"
                    height="18"
                    color="#D32F2F"
                  />
                  <span>Delete</span>
                </>
              )}
            </div>
          </div>,
          document.body,
        )}

      <DocumentPreviewPanel
        isOpen={isPreviewOpen}
        onClose={handleClosePreview}
        assessment={previewAssessment}
      />
    </>
  );
});

// Set display name for debugging
ClientDocumentsData.displayName = "ClientDocumentsData";

export default ClientDocumentsData;